/**
 * Motor de sincronización.
 *
 * La app guarda en el celular y sigue. Este módulo se encarga, aparte, de que
 * lo guardado llegue a Google Sheets y de traer lo que hizo el otro
 * dispositivo. Si no hay señal, espera. Si falla, reintenta. El usuario no se
 * entera y no pierde nada.
 *
 * ── POR QUÉ CUATRO DISPARADORES ─────────────────────────────────────────────
 *
 * Lo ideal sería el evento `sync` del Service Worker: dispara solo cuando
 * vuelve la conexión, incluso con la app cerrada. Pero solo existe en Chrome.
 * En iOS no hay nada parecido. Así que se usan cuatro, en cascada, y basta con
 * que uno funcione:
 *
 *   1. evento `sync` del Service Worker   (Chrome Android, app cerrada)
 *   2. evento `online` de la ventana      (volvió la señal, app abierta)
 *   3. `visibilitychange` a visible       (volviste a la app)
 *   4. cada 5 minutos mientras esté abierta
 *
 * ── POR QUÉ NO SE CONFÍA EN navigator.onLine ────────────────────────────────
 *
 * `navigator.onLine` solo dice si hay una interfaz de red activa. Devuelve
 * `true` conectado a un wifi sin internet, o con datos móviles en una zona sin
 * cobertura real. Sirve para saber que estás OFFLINE (si es `false`, seguro no
 * hay), pero nunca para afirmar que estás online. La única prueba de que hay
 * internet es una petición que responde.
 */

import { db } from './db.js';
import { aplicarDelta } from './repositorio.js';
import { ErrorApi } from './api.js';

const INTERVALO_MS = 5 * 60 * 1000;
const LOTE_MAXIMO = 200;          // filas por viaje, para no pasarse del límite
const REINTENTOS_MAXIMOS = 8;

export const ESTADO = {
  REPOSO: 'reposo',
  SINCRONIZANDO: 'sincronizando',
  SIN_CONEXION: 'sin_conexion',
  ERROR: 'error',
};

export class Sincronizador {
  constructor(api) {
    this.api = api;
    this.estado = ESTADO.REPOSO;
    this.ultimoError = null;
    this.ultimaSync = null;
    this.corriendo = false;
    this.oyentes = new Set();
    this._temporizador = null;
    this._manejadores = [];
  }

  /** Suscribe a cambios de estado. Devuelve la función para desuscribir. */
  observar(fn) {
    this.oyentes.add(fn);
    fn(this.instantanea());
    return () => this.oyentes.delete(fn);
  }

  instantanea() {
    return {
      estado: this.estado,
      ultimoError: this.ultimoError,
      ultimaSync: this.ultimaSync,
    };
  }

  _emitir(estado, error = null) {
    this.estado = estado;
    this.ultimoError = error;
    const snap = this.instantanea();
    for (const fn of this.oyentes) {
      try {
        fn(snap);
      } catch (e) {
        console.error('Oyente de sincronización falló:', e);
      }
    }
  }

  /** Engancha los cuatro disparadores. Llamar una vez al arrancar la app. */
  iniciar() {
    const alVolver = () => this.sincronizar({ motivo: 'online' });
    const alMirar = () => {
      if (document.visibilityState === 'visible') {
        this.sincronizar({ motivo: 'visible' });
      }
    };

    window.addEventListener('online', alVolver);
    document.addEventListener('visibilitychange', alMirar);
    this._manejadores = [
      () => window.removeEventListener('online', alVolver),
      () => document.removeEventListener('visibilitychange', alMirar),
    ];

    this._temporizador = setInterval(
      () => this.sincronizar({ motivo: 'intervalo' }),
      INTERVALO_MS
    );

    // Registra el evento `sync` del Service Worker si el navegador lo tiene.
    // Es el único que funciona con la app cerrada, así que vale el intento.
    this._registrarSyncDeFondo();

    this.sincronizar({ motivo: 'arranque' });
  }

  detener() {
    clearInterval(this._temporizador);
    this._manejadores.forEach((quitar) => quitar());
    this._manejadores = [];
  }

  async _registrarSyncDeFondo() {
    try {
      if (!('serviceWorker' in navigator)) return;
      const reg = await navigator.serviceWorker.ready;
      if (!('sync' in reg)) return;      // no existe en iOS ni en Firefox
      await reg.sync.register('subir-pendientes');
    } catch {
      // Que no se pueda registrar no es un problema: quedan los otros tres.
    }
  }

  /**
   * Sube lo pendiente y baja lo nuevo.
   *
   * Es reentrante a propósito: si ya hay una sincronización corriendo, esta
   * llamada se ignora. Sin eso, volver a la app con señal intermitente puede
   * disparar tres sincronizaciones a la vez y duplicar el trabajo.
   */
  async sincronizar({ motivo = 'manual' } = {}) {
    if (this.corriendo) return { omitido: true, motivo: 'ya corriendo' };
    if (!this.api.configurada) return { omitido: true, motivo: 'sin configurar' };

    // navigator.onLine miente cuando dice true, pero cuando dice false acierta.
    if (navigator.onLine === false) {
      this._emitir(ESTADO.SIN_CONEXION);
      return { omitido: true, motivo: 'sin conexión' };
    }

    this.corriendo = true;
    this._emitir(ESTADO.SINCRONIZANDO);

    try {
      const cambios = await this._recogerPendientes();
      const desde = await this._ultimaMarca();

      const respuesta = await this.api.llamar('sync', {
        desde,
        dispositivo: localStorage.getItem('id_dispositivo'),
        cambios: cambios.porTabla,
      });

      // Solo se saca de la cola lo que el servidor confirmó. Si algo falló a
      // medias, se queda pendiente y se reintenta. Es la diferencia entre
      // "creo que subió" y "subió".
      if (cambios.claves.length) {
        await db.outbox.bulkDelete(cambios.claves);
      }

      const resumen = await aplicarDelta(respuesta.delta);
      await this._guardarMarca(respuesta.hora);

      this.ultimaSync = Date.now();
      this._emitir(ESTADO.REPOSO);

      return {
        ok: true,
        motivo,
        subidos: cambios.total,
        recibidos: resumen,
      };
    } catch (e) {
      await this._aplazarPendientes(e);
      const sinRed = e instanceof ErrorApi && e.reintentable;
      this._emitir(sinRed ? ESTADO.SIN_CONEXION : ESTADO.ERROR, e.message);
      return { ok: false, error: e.message };
    } finally {
      this.corriendo = false;
    }
  }

  /** Arma el lote a subir leyendo los registros que la cola señala. */
  async _recogerPendientes() {
    const ahora = Date.now();
    const filas = await db.outbox
      .where('proximo_en').belowOrEqual(ahora)
      .limit(LOTE_MAXIMO)
      .toArray();

    const porTabla = {};
    const claves = [];

    for (const fila of filas) {
      const registro = await db[fila.tabla]?.get(fila.registro_id);
      if (!registro) {
        // El registro ya no existe: la entrada de la cola quedó huérfana.
        claves.push(fila.seq);
        continue;
      }
      (porTabla[fila.tabla] ||= []).push(registro);
      claves.push(fila.seq);
    }

    return { porTabla, claves, total: claves.length };
  }

  /**
   * Cuando falla, no se reintenta de inmediato ni para siempre.
   *
   * Espera exponencial con ruido: 2s, 4s, 8s... hasta 5 minutos, más un
   * aleatorio. El ruido evita que, si hay varios pendientes, todos reintenten
   * en el mismo instante y golpeen el servidor a la vez.
   */
  async _aplazarPendientes(error) {
    const ahora = Date.now();
    const filas = await db.outbox.where('proximo_en').belowOrEqual(ahora).toArray();

    for (const fila of filas) {
      const intentos = (fila.intentos || 0) + 1;

      if (intentos >= REINTENTOS_MAXIMOS) {
        // Se rinde, pero NO borra: el dato sigue en el dispositivo y se marca
        // para que la interfaz pueda mostrarlo y el usuario decida.
        await db.outbox.update(fila.seq, {
          intentos,
          proximo_en: ahora + 60 * 60 * 1000,
          ultimo_error: String(error?.message || error),
        });
        continue;
      }

      const espera = Math.min(2 ** intentos * 1000, 5 * 60 * 1000);
      const ruido = Math.random() * espera * 0.3;
      await db.outbox.update(fila.seq, {
        intentos,
        proximo_en: ahora + espera + ruido,
        ultimo_error: String(error?.message || error),
      });
    }
  }

  async _ultimaMarca() {
    const fila = await db.ajustes.where('clave').equals('ultima_sync_ms').first();
    return Number(fila?.valor || 0);
  }

  async _guardarMarca(hora) {
    // Se escribe directo, sin pasar por el repositorio: esta marca es local
    // de cada dispositivo. Si se sincronizara, un dispositivo le impondría al
    // otro su idea de "hasta dónde ya bajé" y se perderían cambios.
    const existente = await db.ajustes.where('clave').equals('ultima_sync_ms').first();
    if (existente) {
      await db.ajustes.update(existente.id, { valor: hora, actualizado_en: Date.now() });
    } else {
      await db.ajustes.add({
        id: `local_ultima_sync`,
        clave: 'ultima_sync_ms',
        valor: hora,
        actualizado_en: Date.now(),
        borrado_en: null,
        dispositivo: localStorage.getItem('id_dispositivo'),
      });
    }
  }
}

/**
 * Pide al navegador que NO borre los datos.
 *
 * Por defecto, IndexedDB es "best effort": bajo presión de almacenamiento el
 * navegador puede borrar TODO el origen de un golpe, sin avisar. Para datos
 * financieros eso es inaceptable. Con `persist()` concedido, el navegador se
 * compromete a no borrarlos salvo que el usuario lo pida explícitamente.
 *
 * Chrome lo concede solo si la app está instalada o si la usas seguido. Si lo
 * niega, la app debe insistir en el respaldo: por eso devuelve el resultado en
 * vez de tragárselo.
 */
export async function pedirPersistencia() {
  if (!navigator.storage?.persist) {
    return { soportado: false, concedido: false };
  }
  const yaEstaba = await navigator.storage.persisted();
  const concedido = yaEstaba || (await navigator.storage.persist());
  let cuota = null;
  try {
    cuota = await navigator.storage.estimate();
  } catch {
    // En algunos navegadores estimate() no está; no es grave.
  }
  return { soportado: true, concedido, cuota };
}
